import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/Header/LanguageSwitcher';
import {
  accepterInvitation,
  definirNouveauMotDePasse,
  verifierJetonInvitation,
  verifierJetonReinitialisation,
} from './motDePasse.api';

const PAYS_RE = /^[a-z]{2}$/;

// Pays du lien d'abord (le lien peut être ouvert sur un autre appareil),
// puis celui du navigateur, puis sn.
const paysDuLien = (search) => {
  const pays = new URLSearchParams(search).get('pays');
  if (pays && PAYS_RE.test(pays)) return pays;
  return localStorage.getItem('selectedCountryCode') || 'sn';
};

// Le même écran sert au lien de réinitialisation et au lien d'invitation
// (Yros/payor_enterprise#319) : seuls les appels et les textes changent.
const MODES = {
  reinitialisation: {
    verifier: verifierJetonReinitialisation,
    definir: definirNouveauMotDePasse,
    textesPropres: [],
    nouvelleDemande: true,
  },
  invitation: {
    verifier: verifierJetonInvitation,
    definir: accepterInvitation,
    // Textes de l'invitation ; les autres restent ceux de la réinitialisation.
    textesPropres: ['titre', 'bonjour', 'valider', 'succes', 'retourConnexion'],
    nouvelleDemande: false,
  },
};

// Aucun branchement sur la raison d'un refus : le texte vient du proxy.
const messageRefus = (data) => (typeof data?.message === 'string' ? data.message : null);

const Reinitialisation = ({ mode = 'reinitialisation' }) => {
  const { t: tCommun } = useTranslation();
  const config = MODES[mode] || MODES.reinitialisation;
  // Seules les clés `reinitialisation.<nom>` listées dans `textesPropres` sont
  // redirigées vers `<mode>.<nom>` ; toute autre clé (autre namespace compris)
  // est traduite telle quelle.
  const t = (cle, options) => {
    const [namespace, nom] = cle.split('.');
    const propre = namespace === 'reinitialisation' && config.textesPropres.includes(nom);
    return tCommun(propre ? `${mode}.${nom}` : cle, options);
  };
  const { jeton } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pays] = useState(() => paysDuLien(location.search));
  const [etat, setEtat] = useState('chargement');
  const [nom, setNom] = useState('');
  const [refus, setRefus] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    let actif = true;
    config.verifier(jeton, pays)
      .then((data) => {
        if (!actif) return;
        if (data?.valide === true) {
          setNom(data.nom || '');
          setEtat('formulaire');
        } else {
          setRefus(messageRefus(data) || t('reinitialisation.erreurTechnique'));
          setEtat('refus');
        }
      })
      .catch((err) => {
        if (!actif) return;
        const refusLocal = err?.response?.status === 400 ? messageRefus(err.response.data) : null;
        if (refusLocal) {
          setRefus(refusLocal);
          setEtat('refus');
        } else {
          setEtat('erreur');
        }
      });
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jeton, pays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (envoi) return;
    if (nouveau !== confirmation) {
      setErreur(t('reinitialisation.differents'));
      return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      const data = await config.definir(jeton, nouveau, pays);
      if (data?.ok === true) {
        localStorage.setItem('selectedCountryCode', pays);
        sessionStorage.setItem('authSuccessMessage', t('reinitialisation.succes'));
        navigate('/login', { replace: true });
        return;
      }
      setRefus(messageRefus(data) || t('reinitialisation.erreurTechnique'));
      setEtat('refus');
    } catch (err) {
      const status = err?.response?.status;
      const message = messageRefus(err?.response?.data);
      if (status === 429) setErreur(t('reinitialisation.tropDeDemandes'));
      else if (status === 400 && message) setErreur(message);
      else setErreur(t('reinitialisation.erreurTechnique'));
    }
    setEnvoi(false);
  };

  const champ = 'block w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all';

  return (
    <div className="w-full flex items-center justify-center p-8 min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-lg rounded-xl p-8">
          <div className="flex items-center justify-between mb-4">
            <a href="/" className="text-4xl text-[--color-primary] font-extrabold inline-flex items-center">YROS</a>
            <LanguageSwitcher />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('reinitialisation.titre')}</h2>

          {etat === 'chargement' && <p className="text-gray-600">{t('reinitialisation.chargement')}</p>}

          {etat === 'erreur' && (
            <div role="alert" className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              {t('reinitialisation.erreurTechnique')}
            </div>
          )}

          {etat === 'refus' && (
            <div className="space-y-6">
              <div role="alert" className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">{refus}</div>
              {config.nouvelleDemande ? (
                <Link to="/mot-de-passe-oublie" className="block text-center text-[--color-primary] hover:underline">
                  {t('reinitialisation.nouvelleDemande')}
                </Link>
              ) : (
                <Link to="/login" className="block text-center text-[--color-primary] hover:underline">
                  {t('invitation.retourConnexion')}
                </Link>
              )}
            </div>
          )}

          {etat === 'formulaire' && (
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <p className="text-gray-600">{t('reinitialisation.bonjour', { nom })}</p>
              {erreur && (
                <div role="alert" className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">{erreur}</div>
              )}
              <div>
                <label htmlFor="formNouveau" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reinitialisation.nouveauLabel')}
                </label>
                <input type="password" id="formNouveau" autoComplete="new-password" value={nouveau}
                  onChange={(e) => setNouveau(e.target.value)} className={champ} />
              </div>
              <div>
                <label htmlFor="formConfirmation" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reinitialisation.confirmationLabel')}
                </label>
                <input type="password" id="formConfirmation" autoComplete="new-password" value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)} className={champ} />
              </div>
              <button type="submit" disabled={envoi}
                className="w-full bg-[--color-primary] hover:bg-[--color-orange] text-white py-3 px-4 rounded-lg font-medium transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed">
                {t('reinitialisation.valider')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reinitialisation;
